/**
 * Server TTS fetch + playback machinery (server-speech-proxy Phase 1).
 *
 * Shared by ttsStore.speakWithServer (tap-to-replay) and the auto-play
 * server streaming session (createServerStreamingTTSSession). Synthesis
 * happens on the backend (`POST /api/speech/tts` — authed, metered at the
 * seam, standard-Neural voice policy enforced server-side); this module
 * fetches the MP3 bytes, materializes a playable source, and plays it via
 * expo-audio.
 *
 * The endpoint is a POST, so players/downloads (GET-only) can't be pointed
 * at the URL: the bytes come through the authed axios client and are
 * written to a cache file (native) or blob URL (web).
 *
 * Cache files use an indexed ring (`fluencia-tts-<n>.mp3`, n in 0..3):
 * auto-play prefetch keeps >= 2 clips alive at once, so a single fixed
 * filename would let a prefetch overwrite the clip that is still playing.
 * A ring of 4 gives cushion while keeping the cache bounded.
 *
 * Spec: docs/specs/mobile/server-autoplay-tts.mobile.md (ATTS-002)
 */

import { Platform } from 'react-native';
import { createAudioPlayer, setAudioModeAsync, type AudioPlayer } from 'expo-audio';
import { File, Paths } from 'expo-file-system';
import { apiClient } from './apiClient';
import { TTS_FETCH_TIMEOUT_MS } from '../config/timeouts';

/** A fetched, playable TTS clip. Call release() when done with it. */
export interface TtsClip {
  uri: string;
  /** Free the backing resource (web blob URL). Idempotent. */
  release: () => void;
}

export interface FetchTtsAudioOptions {
  /** Voice override for the A/B (server still enforces its voice policy). */
  voice?: string;
  /** Message attribution for cost metering. The server records it only when
   *  it is a real server message UUID — optimistic local ids (`ai-…`) are
   *  ignored there, which is why sessionId matters. */
  messageId?: string;
  /** Chat-session attribution label for cost metering (ADR-026). Keeps
   *  auto-play segments (no server message id yet) and local-id replays
   *  attributable at session level instead of vanishing from the ledger. */
  sessionId?: string;
  /** Abort the HTTP request (auto-play cancellation). */
  signal?: AbortSignal;
}

export interface TtsPlaybackResult {
  /** True when the clip played to its natural end. */
  finished: boolean;
  /** Present when playback failed. */
  error?: string;
}

export interface TtsPlayback {
  /** Stop playback and release player + clip. Idempotent. */
  stop: () => void;
  /** Settles when playback finishes, errors, or is stopped. Never rejects. */
  done: Promise<TtsPlaybackResult>;
}

/**
 * Rejection thrown by `fetchTtsAudio` when the per-segment fetch exceeds
 * `TTS_FETCH_TIMEOUT_MS`. Distinguishable by `name` so callers can log the
 * stall recovery without importing this module's internals (the auto-play
 * session mocks the whole module in tests).
 */
export class TtsFetchTimeoutError extends Error {
  constructor() {
    super('TTS fetch timed out');
    this.name = 'TtsFetchTimeoutError';
  }
}

/**
 * Build an abort signal that fires when EITHER the caller's `signal` (auto-play
 * cancel) OR the internal timeout aborts — the timeout ANDs with the caller's
 * cancel rather than replacing it. Returns the composed signal plus a `dispose`
 * that detaches the listeners once the request settles.
 *
 * When no caller signal is supplied, the timeout's own signal is forwarded
 * directly (no wrapper needed).
 */
function linkAbortSignals(
  caller: AbortSignal | undefined,
  timeout: AbortSignal,
): { signal: AbortSignal; dispose: () => void } {
  if (!caller) return { signal: timeout, dispose: () => {} };
  const controller = new AbortController();
  if (caller.aborted || timeout.aborted) {
    controller.abort();
    return { signal: controller.signal, dispose: () => {} };
  }
  const onAbort = () => controller.abort();
  caller.addEventListener('abort', onAbort);
  timeout.addEventListener('abort', onAbort);
  return {
    signal: controller.signal,
    dispose: () => {
      caller.removeEventListener('abort', onAbort);
      timeout.removeEventListener('abort', onAbort);
    },
  };
}

const CACHE_FILE_PREFIX = 'fluencia-tts-';
const CACHE_RING_SIZE = 4;
let ringIndex = 0;

let audioModeConfigured = false;

/**
 * One-time audio mode setup shared by every server-TTS playback: replies
 * must stay audible with the iOS silent switch on (matches the Azure SDK
 * path's behavior). Safe to call repeatedly.
 */
export async function configureTtsAudioMode(): Promise<void> {
  if (audioModeConfigured) return;
  audioModeConfigured = true;
  await setAudioModeAsync({ playsInSilentMode: true }).catch(() => {});
}

/**
 * Synthesize `text` via POST /api/speech/tts and return a playable clip.
 * Throws on HTTP failure; rethrows the axios cancellation error when the
 * caller's `signal` aborts, and throws `TtsFetchTimeoutError` when the
 * per-segment `TTS_FETCH_TIMEOUT_MS` elapses first.
 *
 * The shared axios client's global timeout (~85s) is far too long for a
 * single TTS segment, so this bounds each POST independently: on timeout the
 * request is aborted AND the returned promise rejects, so a hung segment can
 * never leave the auto-play pump waiting forever.
 */
export async function fetchTtsAudio(
  text: string,
  options: FetchTtsAudioOptions = {},
): Promise<TtsClip> {
  const { voice, messageId, sessionId, signal } = options;

  const timeoutController = new AbortController();
  const { signal: requestSignal, dispose } = linkAbortSignals(signal, timeoutController.signal);

  let timeoutTimer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutTimer = setTimeout(() => {
      timeoutController.abort();
      reject(new TtsFetchTimeoutError());
    }, TTS_FETCH_TIMEOUT_MS);
  });

  const request = apiClient.post<ArrayBuffer>(
    '/speech/tts',
    { text, ...(voice ? { voice } : {}), ...(messageId ? { messageId } : {}), ...(sessionId ? { sessionId } : {}) },
    { responseType: 'arraybuffer', signal: requestSignal },
  );
  // If the timeout wins the race, the aborted request still settles (rejects)
  // afterwards — swallow that trailing rejection so it isn't left unhandled.
  request.catch(() => {});

  let response: Awaited<typeof request>;
  try {
    response = await Promise.race([request, timeout]);
  } finally {
    if (timeoutTimer) clearTimeout(timeoutTimer);
    dispose();
  }

  if (Platform.OS === 'web') {
    const blobUrl = URL.createObjectURL(new Blob([response.data], { type: 'audio/mpeg' }));
    let released = false;
    return {
      uri: blobUrl,
      release: () => {
        if (released) return;
        released = true;
        try { URL.revokeObjectURL(blobUrl); } catch { /* no-op */ }
      },
    };
  }

  const slot = ringIndex;
  ringIndex = (ringIndex + 1) % CACHE_RING_SIZE;
  const file = new File(Paths.cache, `${CACHE_FILE_PREFIX}${slot}.mp3`);
  if (!file.exists) file.create();
  file.write(new Uint8Array(response.data));
  // Ring files are reused by index — nothing to free per clip.
  return { uri: file.uri, release: () => {} };
}

/**
 * Play a fetched clip. Returns a handle whose `done` promise settles on
 * natural finish, playback error, or stop(); settling releases the player
 * and the clip exactly once.
 *
 * `onProgress` fires on every non-terminal status update (expo-audio emits
 * these ~every 500ms while playing) — a liveness signal the auto-play session
 * watchdog uses to distinguish a long-but-healthy segment from a wedged
 * player whose `done` never settles.
 */
export function playTtsClip(clip: TtsClip, onProgress?: () => void): TtsPlayback {
  let resolveDone!: (result: TtsPlaybackResult) => void;
  const done = new Promise<TtsPlaybackResult>((resolve) => {
    resolveDone = resolve;
  });

  let player: AudioPlayer | null = createAudioPlayer({ uri: clip.uri });

  const settle = (result: TtsPlaybackResult) => {
    if (!player) return;
    const current = player;
    player = null;
    try { current.remove(); } catch { /* already released */ }
    clip.release();
    resolveDone(result);
  };

  player.addListener('playbackStatusUpdate', (status) => {
    if (status.error != null) {
      settle({ finished: false, error: String(status.error) });
      return;
    }
    if (status.didJustFinish) {
      settle({ finished: true });
      return;
    }
    // Still playing — report liveness so the watchdog doesn't fire mid-segment.
    onProgress?.();
  });
  player.play();

  return {
    stop: () => settle({ finished: false }),
    done,
  };
}
